import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';

import {
  AdminTable,
  AdminTableHeaderSearch,
  AdminTableNameEmailHeader,
  AdminTableNameEmailRow,
  ADMIN_TABLE_MESSAGE_BODY_ROWS,
  ADMIN_TABLE_MIN_PAGE_SIZE,
  ADMIN_TABLE_PAGE_SIZES,
  ADMIN_TABLE_ROW_HEIGHT,
  adminTableMemberGridStyles,
  adminTableStyles,
  TablePager,
} from '../../components/adminTable';
import { useGroupMemberships, useGroups } from '../../hooks/queries/useGroups';
import { GroupListItem, GroupsPageSize } from '../../services/groupsService';
import { networkService } from '../../services/network';
import { colors } from '../../theme/colors';
import { layout, spacing } from '../../theme/spacing';

const WIDE_LAYOUT_MIN_WIDTH = 900;
// Stacked, the two grids read as one long table unless the seam between them is
// wider than the gaps inside a grid (row rules, pager rule).
const STACKED_SECTION_GAP = 32;

interface GroupsTabProps {
  enabled: boolean;
}

export const GroupsTab: React.FC<GroupsTabProps> = ({ enabled }) => {
  const { width, height } = useWindowDimensions();
  const isNativePhone = Platform.OS !== 'web' && Math.min(width, height) < 600;
  const isWide = width >= WIDE_LAYOUT_MIN_WIDTH && !isNativePhone;
  const [isOnline, setIsOnline] = useState(networkService.isConnected());

  const groupSearchRef = useRef<TextInput>(null);
  const memberSearchRef = useRef<TextInput>(null);
  const [focusedSearch, setFocusedSearch] = useState<
    'groups' | 'members' | null
  >(null);

  const [groupSearch, setGroupSearch] = useState('');
  const [debouncedGroupSearch, setDebouncedGroupSearch] = useState('');
  const [groupPage, setGroupPage] = useState(1);
  const [groupPageSize, setGroupPageSize] = useState<GroupsPageSize>(
    ADMIN_TABLE_MIN_PAGE_SIZE,
  );
  const [selectedGroup, setSelectedGroup] = useState<GroupListItem | null>(
    null,
  );
  const [memberSearch, setMemberSearch] = useState('');
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('');
  const [memberPage, setMemberPage] = useState(1);
  const [memberPageSize, setMemberPageSize] = useState<GroupsPageSize>(
    ADMIN_TABLE_MIN_PAGE_SIZE,
  );
  const groupSearchForSelectionRef = useRef(debouncedGroupSearch);

  useEffect(() => networkService.addListener(setIsOnline), []);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = groupSearch.trim();
      if (nextSearch !== debouncedGroupSearch) {
        setDebouncedGroupSearch(nextSearch);
        setGroupPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedGroupSearch, groupSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const nextSearch = memberSearch.trim();
      if (nextSearch !== debouncedMemberSearch) {
        setDebouncedMemberSearch(nextSearch);
        setMemberPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [debouncedMemberSearch, memberSearch]);

  const groupParams = useMemo(
    () => ({
      page: groupPage,
      pageSize: groupPageSize,
      ...(debouncedGroupSearch ? { search: debouncedGroupSearch } : {}),
    }),
    [debouncedGroupSearch, groupPage, groupPageSize],
  );
  const groupsQuery = useGroups(groupParams, enabled && isOnline);

  const memberParams = useMemo(
    () => ({
      page: memberPage,
      pageSize: memberPageSize,
      ...(debouncedMemberSearch ? { search: debouncedMemberSearch } : {}),
    }),
    [debouncedMemberSearch, memberPage, memberPageSize],
  );
  const membershipsQuery = useGroupMemberships(
    selectedGroup?.groupId ?? null,
    memberParams,
    enabled && isOnline && selectedGroup !== null,
  );

  const wasOnline = React.useRef(isOnline);
  useEffect(() => {
    if (!wasOnline.current && isOnline && enabled) {
      groupsQuery.refetch();
      if (selectedGroup) {
        membershipsQuery.refetch();
      }
    }
    wasOnline.current = isOnline;
  }, [enabled, groupsQuery, isOnline, membershipsQuery, selectedGroup]);

  useEffect(() => {
    if (!selectedGroup || !groupsQuery.data || groupsQuery.isPlaceholderData) {
      return;
    }
    const current = groupsQuery.data.groups.find(
      group => group.groupId === selectedGroup.groupId,
    );
    if (current) {
      if (
        current.displayName !== selectedGroup.displayName ||
        current.memberCount !== selectedGroup.memberCount
      ) {
        setSelectedGroup(current);
      }
      groupSearchForSelectionRef.current = debouncedGroupSearch;
      return;
    }
    const searchChanged =
      groupSearchForSelectionRef.current !== debouncedGroupSearch;
    groupSearchForSelectionRef.current = debouncedGroupSearch;
    if (searchChanged && debouncedGroupSearch) {
      setSelectedGroup(null);
      setMemberSearch('');
      setDebouncedMemberSearch('');
      setMemberPage(1);
      setMemberPageSize(ADMIN_TABLE_MIN_PAGE_SIZE);
    }
  }, [
    debouncedGroupSearch,
    groupsQuery.data,
    groupsQuery.isPlaceholderData,
    selectedGroup,
  ]);

  useEffect(() => {
    if (!groupsQuery.data || groupsQuery.isPlaceholderData) {
      return;
    }
    const totalPages = Math.max(groupsQuery.data.pagination.totalPages, 1);
    if (groupPage > totalPages) {
      setGroupPage(totalPages);
    }
  }, [groupPage, groupsQuery.data, groupsQuery.isPlaceholderData]);

  useEffect(() => {
    if (!membershipsQuery.data || membershipsQuery.isPlaceholderData) {
      return;
    }
    const totalPages = Math.max(membershipsQuery.data.pagination.totalPages, 1);
    if (memberPage > totalPages) {
      setMemberPage(totalPages);
    }
  }, [memberPage, membershipsQuery.data, membershipsQuery.isPlaceholderData]);

  const selectGroup = (group: GroupListItem) => {
    if (selectedGroup?.groupId === group.groupId) {
      return;
    }
    setSelectedGroup(group);
    setMemberSearch('');
    setDebouncedMemberSearch('');
    setMemberPage(1);
    setMemberPageSize(ADMIN_TABLE_MIN_PAGE_SIZE);
  };

  const changeGroupPage = (nextPage: number) => {
    if (nextPage < 1 || nextPage === groupPage) {
      return;
    }
    setGroupPage(nextPage);
  };

  const changeGroupPageSize = (nextSize: number) => {
    const pageSize = nextSize as GroupsPageSize;
    if (pageSize === groupPageSize) {
      return;
    }
    setGroupPageSize(pageSize);
    setGroupPage(1);
  };

  const changeMemberPageSize = (nextSize: number) => {
    const pageSize = nextSize as GroupsPageSize;
    if (pageSize === memberPageSize) {
      return;
    }
    setMemberPageSize(pageSize);
    setMemberPage(1);
  };

  const showMemberTools =
    selectedGroup !== null &&
    (selectedGroup.memberCount > ADMIN_TABLE_MIN_PAGE_SIZE ||
      memberSearch.length > 0);
  const groupBodyRowCount = groupsQuery.data?.groups.length ?? 0;
  const memberBodyRowCount = membershipsQuery.data?.memberships.length ?? 0;
  const groupBodyShowsRows =
    isOnline &&
    !groupsQuery.isLoading &&
    !groupsQuery.isPlaceholderData &&
    !groupsQuery.error &&
    groupBodyRowCount > 0;
  const memberBodyShowsRows =
    selectedGroup !== null &&
    isOnline &&
    !membershipsQuery.isLoading &&
    !membershipsQuery.isPlaceholderData &&
    !membershipsQuery.error &&
    memberBodyRowCount > 0;
  const cappedBodyRows = (rowCount: number, showsRows: boolean) =>
    showsRows
      ? Math.min(rowCount, ADMIN_TABLE_MIN_PAGE_SIZE)
      : ADMIN_TABLE_MESSAGE_BODY_ROWS;
  const groupBodyHeight =
    cappedBodyRows(groupBodyRowCount, groupBodyShowsRows) *
    ADMIN_TABLE_ROW_HEIGHT;
  const memberBodyHeight =
    cappedBodyRows(memberBodyRowCount, memberBodyShowsRows) *
    ADMIN_TABLE_ROW_HEIGHT;
  const groupBodyScrolls =
    groupBodyShowsRows && groupBodyRowCount > ADMIN_TABLE_MIN_PAGE_SIZE;
  const memberBodyScrolls =
    memberBodyShowsRows && memberBodyRowCount > ADMIN_TABLE_MIN_PAGE_SIZE;

  const groupBody = () => {
    if (!isOnline) {
      return (
        <View style={adminTableMemberGridStyles.stateRow}>
          <Text style={adminTableMemberGridStyles.errorText}>
            Couldn't load groups.
          </Text>
          <TouchableOpacity
            onPress={() => groupsQuery.refetch()}
            accessibilityRole="button"
          >
            <Text style={adminTableMemberGridStyles.retryText}>Try again</Text>
          </TouchableOpacity>
        </View>
      );
    }
    if (groupsQuery.isLoading || groupsQuery.isPlaceholderData) {
      return (
        <Text style={adminTableMemberGridStyles.stateText}>
          Loading groups…
        </Text>
      );
    }
    if (groupsQuery.error) {
      return (
        <View style={adminTableMemberGridStyles.stateRow}>
          <Text style={adminTableMemberGridStyles.errorText}>
            Couldn't load groups.
          </Text>
          <TouchableOpacity
            onPress={() => groupsQuery.refetch()}
            accessibilityRole="button"
          >
            <Text style={adminTableMemberGridStyles.retryText}>Try again</Text>
          </TouchableOpacity>
        </View>
      );
    }
    if (groupsQuery.data?.groups.length === 0) {
      return (
        <Text style={adminTableMemberGridStyles.stateText}>
          {debouncedGroupSearch
            ? `No groups match "${debouncedGroupSearch}".`
            : 'No groups yet. Groups are created automatically when users sign in.'}
        </Text>
      );
    }
    return groupsQuery.data?.groups.map(group => {
      const isSelected = selectedGroup?.groupId === group.groupId;
      const webRowProps =
        Platform.OS === 'web'
          ? ({
              'aria-controls': 'group-members-grid',
              'aria-selected': isSelected,
              role: 'option',
              tabIndex: 0,
              onKeyDown: (event: {
                key?: string;
                preventDefault?: () => void;
              }) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault?.();
                  selectGroup(group);
                }
              },
            } as Record<string, unknown>)
          : {};

      return (
        <TouchableOpacity
          key={group.groupId}
          style={[
            adminTableStyles.row,
            adminTableMemberGridStyles.gridRow,
            isSelected && styles.selectedGroupRow,
          ]}
          onPress={() => selectGroup(group)}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel={`${group.displayName}, ${group.memberCount} users`}
          accessibilityState={{ selected: isSelected }}
          testID={`group-row-${group.groupId}`}
          {...webRowProps}
        >
          <Text style={adminTableStyles.primaryCell} numberOfLines={1}>
            {group.displayName}
          </Text>
          <Text
            style={[
              adminTableStyles.secondaryCell,
              adminTableMemberGridStyles.rightAligned,
            ]}
            numberOfLines={1}
          >
            {group.memberCount}
          </Text>
        </TouchableOpacity>
      );
    });
  };

  const memberBody = () => {
    if (!selectedGroup) {
      return (
        <Text style={adminTableMemberGridStyles.stateText}>
          Select a group to view its members.
        </Text>
      );
    }
    if (!isOnline) {
      return (
        <View style={adminTableMemberGridStyles.stateRow}>
          <Text style={adminTableMemberGridStyles.errorText}>
            Couldn't load members.
          </Text>
          <TouchableOpacity
            onPress={() => membershipsQuery.refetch()}
            accessibilityRole="button"
          >
            <Text style={adminTableMemberGridStyles.retryText}>Try again</Text>
          </TouchableOpacity>
        </View>
      );
    }
    if (membershipsQuery.isLoading || membershipsQuery.isPlaceholderData) {
      return (
        <Text style={adminTableMemberGridStyles.stateText}>
          Loading members…
        </Text>
      );
    }
    if (membershipsQuery.error) {
      return (
        <View style={adminTableMemberGridStyles.stateRow}>
          <Text style={adminTableMemberGridStyles.errorText}>
            Couldn't load members.
          </Text>
          <TouchableOpacity
            onPress={() => membershipsQuery.refetch()}
            accessibilityRole="button"
          >
            <Text style={adminTableMemberGridStyles.retryText}>Try again</Text>
          </TouchableOpacity>
        </View>
      );
    }
    if (membershipsQuery.data?.memberships.length === 0) {
      return (
        <Text style={adminTableMemberGridStyles.stateText}>
          {debouncedMemberSearch
            ? `No members match "${debouncedMemberSearch}".`
            : 'No users'}
        </Text>
      );
    }
    return membershipsQuery.data?.memberships.map(member => (
      <AdminTableNameEmailRow
        key={member.userId}
        name={member.displayName}
        email={member.email}
        accessibilityLabel={`${member.displayName}, ${member.email}`}
        testID={`member-row-${member.userId}`}
      />
    ));
  };

  const content = (
    <View
      style={[styles.container, isWide ? styles.wide : styles.stacked]}
      testID="groups-tab"
    >
      <View
        style={[styles.pane, isWide ? styles.widePane : styles.stackedPane]}
        testID="groups-pane"
      >
        <Text style={styles.heading}>Groups</Text>
        <AdminTable
          testID="groups-grid"
          accessibilityLabel="Groups grid"
          pager={
            groupBodyShowsRows &&
            groupsQuery.data &&
            groupsQuery.data.pagination.totalCount >
              ADMIN_TABLE_MIN_PAGE_SIZE ? (
              <TablePager
                label="Groups"
                page={groupPage}
                pageSize={groupPageSize}
                pageSizes={ADMIN_TABLE_PAGE_SIZES}
                pagination={groupsQuery.data.pagination}
                onPageChange={changeGroupPage}
                onPageSizeChange={changeGroupPageSize}
              />
            ) : null
          }
        >
          <AdminTableNameEmailHeader trailingLabel="User Count">
            <AdminTableHeaderSearch
              inputRef={groupSearchRef}
              value={groupSearch}
              onChangeText={setGroupSearch}
              onFocus={() => setFocusedSearch('groups')}
              onBlur={() => setFocusedSearch(null)}
              focused={focusedSearch === 'groups'}
              placeholder="Search by group name..."
              accessibilityLabel="Search groups"
              searchBoxTestID="groups-search-box"
              searchInputTestID="groups-search"
              isSearching={groupsQuery.isFetching && !groupsQuery.isLoading}
            />
          </AdminTableNameEmailHeader>
          <ScrollView
            style={[
              adminTableMemberGridStyles.gridBody,
              styles.gridBodyScroll,
              { height: groupBodyHeight },
            ]}
            contentContainerStyle={adminTableMemberGridStyles.gridBodyContent}
            scrollEnabled={groupBodyScrolls}
            nestedScrollEnabled
            testID="groups-grid-body"
            {...(Platform.OS === 'web'
              ? ({ role: 'listbox' } as Record<string, unknown>)
              : {})}
          >
            {groupBody()}
          </ScrollView>
        </AdminTable>
      </View>

      <View
        style={[
          styles.pane,
          isWide ? styles.widePane : styles.stackedPane,
          !isWide && styles.stackedSection,
        ]}
        nativeID="group-members-grid"
        testID="members-pane"
        accessibilityLabel={
          selectedGroup ? `Members of ${selectedGroup.displayName}` : 'Members'
        }
      >
        <Text style={styles.heading}>
          {selectedGroup
            ? `Members of ${selectedGroup.displayName}`
            : 'Members'}
        </Text>
        {selectedGroup && membershipsQuery.data ? (
          <Text
            style={styles.screenReaderText}
            accessibilityLiveRegion="polite"
          >
            Loaded {membershipsQuery.data.pagination.totalCount} members of{' '}
            {selectedGroup.displayName}
          </Text>
        ) : null}
        <AdminTable
          testID="members-grid"
          accessibilityLabel="Members grid"
          pager={
            showMemberTools &&
            memberBodyShowsRows &&
            membershipsQuery.data &&
            membershipsQuery.data.pagination.totalCount >
              ADMIN_TABLE_MIN_PAGE_SIZE ? (
              <TablePager
                label="Members"
                page={memberPage}
                pageSize={memberPageSize}
                pageSizes={ADMIN_TABLE_PAGE_SIZES}
                pagination={membershipsQuery.data.pagination}
                onPageChange={setMemberPage}
                onPageSizeChange={changeMemberPageSize}
              />
            ) : null
          }
        >
          <AdminTableNameEmailHeader trailingLabel="Email">
            {showMemberTools ? (
              <AdminTableHeaderSearch
                inputRef={memberSearchRef}
                value={memberSearch}
                onChangeText={setMemberSearch}
                onFocus={() => setFocusedSearch('members')}
                onBlur={() => setFocusedSearch(null)}
                focused={focusedSearch === 'members'}
                placeholder="Search by name or email..."
                accessibilityLabel={`Search members of ${selectedGroup?.displayName}`}
                searchBoxTestID="members-search-box"
                searchInputTestID="members-search"
                isSearching={
                  membershipsQuery.isFetching && !membershipsQuery.isLoading
                }
              />
            ) : null}
          </AdminTableNameEmailHeader>
          <ScrollView
            style={[
              adminTableMemberGridStyles.gridBody,
              styles.gridBodyScroll,
              { height: memberBodyHeight },
            ]}
            contentContainerStyle={adminTableMemberGridStyles.gridBodyContent}
            scrollEnabled={memberBodyScrolls}
            nestedScrollEnabled
            testID="members-grid-body"
          >
            {memberBody()}
          </ScrollView>
        </AdminTable>
      </View>
    </View>
  );

  return (
    <ScrollView
      style={styles.scrollContainer}
      contentContainerStyle={styles.scrollContent}
      // The page shell locks the document to the viewport (`overflow: hidden`
      // on html/body/#root). Side-by-side and stacked both have to scroll here,
      // or a short window clips the tables with no scrollbar.
      // keyboardShouldPersistTaps: the ScrollView must not eat the tap that
      // focuses an inline search field, or the soft keyboard never opens.
      keyboardShouldPersistTaps="handled"
      keyboardDismissMode="on-drag"
      testID="groups-tab-scroll"
    >
      {content}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 1600,
    minWidth: 0,
    alignSelf: 'stretch',
    alignItems: 'flex-start',
  },
  wide: {
    flexDirection: 'row',
    width: '100%',
    minWidth: 0,
    gap: spacing.sectionMarginBottom,
  },
  stacked: {
    flexDirection: 'column',
    gap: STACKED_SECTION_GAP,
  },
  pane: {
    minWidth: 0,
  },
  widePane: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    alignSelf: 'flex-start',
  },
  stackedPane: {
    width: '100%',
  },
  /**
   * Only the second stacked pane gets the rule; side by side, the column gap
   * already separates them.
   */
  stackedSection: {
    borderTopWidth: 1,
    borderTopColor: colors.borderMedium,
    paddingTop: STACKED_SECTION_GAP,
  },
  heading: {
    color: colors.textPrimary,
    fontSize: 15,
    fontWeight: '600',
    marginBottom: spacing.sm,
  },
  selectedGroupRow: {
    backgroundColor: colors.surfaceAccentMuted,
    borderLeftWidth: 3,
    borderLeftColor: colors.primary,
    paddingLeft: layout.users.tableSectionHorizontalPadding - 3,
  },
  screenReaderText: {
    position: 'absolute',
    width: 1,
    height: 1,
    overflow: 'hidden',
    opacity: 0.01,
  },
  scrollContainer: {
    width: '100%',
    flex: 1,
  },
  scrollContent: {
    width: '100%',
    flexGrow: 0,
    paddingBottom: spacing.sectionMarginBottom,
  },
  /**
   * RN Web's ScrollView base style is flexGrow/flexShrink 1. Inside a pane
   * that is only as tall as the viewport, that growth consumes the pane and
   * the pager (rows-per-page, Previous/Next) is clipped underneath.
   */
  gridBodyScroll: {
    flexGrow: 0,
    flexShrink: 0,
  },
});
